import { useMyContractTerms } from './hooks/useMerchants';
import { CONTRACT_TYPE_LABELS, INVOICING_CADENCE_LABELS } from './types';

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Contract-terms money (platform fees) arrives in minor units (kobo/pence). Amounts are
// shown with the platform's primary currency symbol, consistent with the rest of Osprey's
// merchant views (metrics, payouts) which present figures in ₦.
function formatMinor(amount: number | undefined) {
  if (amount == null) return '—';
  return `₦${(amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children}</dd>
    </div>
  );
}

/**
 * PE-2098 — read-only view of the logged-in merchant's OWN contract terms.
 * There are deliberately no edit controls: contract terms are TCS-managed and the
 * backend rejects contract-field writes from any merchant-scoped actor.
 */
export default function MyContract() {
  const { data: contract, isLoading, error } = useMyContractTerms();

  if (isLoading) return <div className="py-12 text-center text-foreground">Loading…</div>;
  if (error || !contract) {
    return (
      <div className="py-12 text-center text-red-500">
        We couldn&apos;t load your contract terms. Please try again shortly.
      </div>
    );
  }

  const isCommission = contract.contractType === 'COMMISSION';
  const schedule = contract.invoicingSchedule;
  const commissionPct = `${(parseFloat(contract.commissionCharge) * 100).toFixed(1)}%`;

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">My Contract</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your billing arrangement with The Collective Synergy. These terms are managed by TCS —
          contact your account manager if anything looks incorrect.
        </p>
      </div>

      {/* Billing arrangement */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Billing Arrangement</h2>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Contract Type">
            {CONTRACT_TYPE_LABELS[contract.contractType] ?? contract.contractType}
          </Row>
          <Row label="Commission">{isCommission ? commissionPct : '—'}</Row>
          <Row label="Invoicing Cadence">
            {INVOICING_CADENCE_LABELS[contract.invoicingCadence] ?? contract.invoicingCadence}
          </Row>
        </dl>
      </div>

      {/* Platform-fee schedule — only meaningful for non-commission (outright / instalment) merchants */}
      {!isCommission && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-base font-semibold text-foreground mb-1">Platform Fee Schedule</h2>
          <p className="text-sm text-muted-foreground mb-4">
            How your platform fees are billed under your {CONTRACT_TYPE_LABELS[contract.contractType]?.toLowerCase()} contract.
          </p>
          {schedule ? (
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row label="One-time Fee">{formatMinor(schedule.oneTimeFeeAmount)}</Row>
              <Row label="One-time Fee Due">{formatDate(schedule.oneTimeFeeDate ?? null)}</Row>
              <Row label="Recurring Fee">{formatMinor(schedule.recurringFeeAmount)}</Row>
              <Row label="Recurring Every">
                {schedule.recurringIntervalDays != null ? `${schedule.recurringIntervalDays} days` : '—'}
              </Row>
              {contract.contractType === 'OUTRIGHT_INSTALMENT' && (
                <Row label="Instalments">
                  {schedule.instalmentCount != null ? `${schedule.instalmentCount}` : '—'}
                </Row>
              )}
            </dl>
          ) : (
            <p className="text-sm text-foreground">No platform-fee schedule has been set for your account yet.</p>
          )}
        </div>
      )}

      {/* Contract dates */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Contract Period</h2>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Start Date">{formatDate(contract.contractStartDate)}</Row>
          <Row label="End Date">{formatDate(contract.contractEndDate)}</Row>
          <Row label="Next Review">{formatDate(contract.nextContractReviewDate)}</Row>
        </dl>
        <p className="text-xs text-muted-foreground mt-4">
          Terms last updated {formatDate(contract.updatedAt)}.
        </p>
      </div>
    </div>
  );
}
